import { useId, type ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import styles from './Field.module.css'

interface FieldProps {
  label: string
  value: string
  onChange?: (value: string) => void
  placeholder?: string
  hint?: ReactNode
  /** Inline error: 13px red text with an error icon. */
  error?: string
  multiline?: boolean
  type?: string
  width?: number | string
  autoFocus?: boolean
}

export function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
  error,
  multiline,
  type = 'text',
  width,
  autoFocus,
}: FieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  const common = {
    id,
    value,
    placeholder,
    autoFocus,
    'data-autofocus': autoFocus ? '' : undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  }
  return (
    <div className={styles.field} style={{ width }}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      {multiline ? (
        <textarea
          {...common}
          rows={4}
          className={cx(styles.control, styles.textarea)}
          onChange={(e) => onChange?.(e.target.value)}
        />
      ) : (
        <input
          {...common}
          type={type}
          className={cx(styles.control, styles.input)}
          onChange={(e) => onChange?.(e.target.value)}
        />
      )}
      {error ? (
        <span id={`${id}-error`} className={styles.error}>
          <Icon name="error" size={16} />
          {error}
        </span>
      ) : (
        hint && (
          <span id={`${id}-hint`} className={styles.hint}>
            {hint}
          </span>
        )
      )}
    </div>
  )
}
